import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";

const JlgCollectionSheetReport = () => {
  const { adminUser } = useAdminAuth();
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCro, setSelectedCro] = useState(null);
  const [croOptions, setCroOptions] = useState([]);

  // Update the print styles
  useEffect(() => {
    // Add print styles
    const style = document.createElement("style");
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-section, .print-section * {
          visibility: visible;
        }
        .print-section {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
        }
        .original-table {
          display: none;
        }
        .dashboard-over, .container-fluid {
          padding: 0 !important;
          margin: 0 !important;
          overflow: visible !important;
        }
        .btn, h1, h3, h4, .filter-controls {
          display: none !important;
        }
        
        /* Fix for table borders in print */
        .print-section table {
          border-collapse: collapse;
          width: 100%;
          font-size: 8pt;
          table-layout: auto;
        }
        .print-section table, 
        .print-section th, 
        .print-section td {
          border: 1px solid black !important;
          padding: 3px !important;
        }
        .print-section th {
          font-weight: bold;
          white-space: nowrap;
        }
        
        /* Ensure page breaks properly */
        .report-sheet {
          page-break-after: always;
          margin-bottom: 0 !important;
        }
        
        /* Adjust page margins */
        @page {
          size: landscape;
          margin: 10mm;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const getCrosByManagerId = useCallback(async (managerId) => {
    try {
      const response = await axios.get(`${BASE_URL}/api/getCrosByManagerId`, {
        params: { managerId },
      });
      if (response.data.message) {
        const formattedOptions = response.data.message.map((cro) => ({
          value: cro.id,
          label: cro.username,
        }));
        setCroOptions(formattedOptions);
      } else {
        setCroOptions([]);
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    getCrosByManagerId(adminUser.user.id);
  }, [getCrosByManagerId, adminUser.user.id]);

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [showReport, setShowReport] = useState(false);

  const handleViewReport = async () => {
    // Validate all input fields
    if (!fromDate) {
      alert("Please select From Date");
      return;
    }

    if (!toDate) {
      alert("Please select To Date");
      return;
    }

    if (!selectedCro) {
      alert("Please select an Employee");
      return;
    }

    try {
      setLoading(true);
      // Fetch data from API based on inputs
      const response = await axios.get(
        `${BASE_URL}/api/getJlgCollectionSheetReport`,
        {
          params: {
            fromDate,
            toDate,
            manager_id: selectedCro.value,
            role: adminUser.userType,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.reports);
        setShowReport(true);
      } else {
        alert("No data found for the selected criteria");
      }
    } catch (error) {
      console.error("Error fetching report data:", error);
      alert("Failed to fetch report data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Update the handleDownloadPdf function
  const handleDownloadPdf = () => {
    try {
      setLoading(true);
      const reportSheets = document.querySelectorAll('.report-sheet');
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4"
      });

      // Process each report sheet (center) separately
      const processSheet = async (index) => {
        if (index >= reportSheets.length) {
          pdf.save(`JLG_Collection_Sheet_${fromDate}_to_${toDate}.pdf`);
          setLoading(false);
          return;
        }

        const sheet = reportSheets[index];

        // Apply temporary styles for better PDF capture
        const tempStyle = document.createElement('style');
        tempStyle.innerHTML = `
          .print-section table {
            font-size: 7pt !important;
            width: 100% !important;
          }
          .print-section th, .print-section td {
            padding: 2px !important;
          }
        `;
        document.head.appendChild(tempStyle);

        // Capture the current sheet
        const canvas = await html2canvas(sheet, {
          scale: 2,
          useCORS: true,
          logging: false,
          letterRendering: true,
          allowTaint: true,
          backgroundColor: "var(--bg-card)",
        });

        // Remove temporary styles
        document.head.removeChild(tempStyle);

        // Add new page for each sheet except the first one
        if (index > 0) {
          pdf.addPage();
        }

        // Calculate dimensions
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 5; // 5mm margins

        // Calculate image dimensions to fit the page with margins
        const availableWidth = pageWidth - (2 * margin);
        const availableHeight = pageHeight - (2 * margin);

        // Calculate scaling to fit the content
        const imgRatio = canvas.height / canvas.width;
        const pageRatio = availableHeight / availableWidth;

        let imgWidth = availableWidth;
        let imgHeight = imgWidth * imgRatio;

        // If image height exceeds available height, scale down
        if (imgHeight > availableHeight) {
          imgHeight = availableHeight;
          imgWidth = imgHeight / imgRatio;
        }

        // Center the image on the page
        const xOffset = margin + (availableWidth - imgWidth) / 2;
        const yOffset = margin + (availableHeight - imgHeight) / 2;

        // Add the image to the PDF
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 1.0),
          'JPEG',
          xOffset,
          yOffset,
          imgWidth,
          imgHeight
        );

        // Process next sheet
        await processSheet(index + 1);
      };

      // Start processing from the first sheet
      processSheet(0);

    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
      setLoading(false);
    }
  };

  // Function to render the formatted report table similar to the image
  const renderFormattedReport = () => {
    if (!reportData || reportData.length === 0) return null;

    // Group data by center and group
    const groupedData = {};
    reportData.forEach((item) => {
      const centerKey = item.centerName;
      if (!groupedData[centerKey]) {
        groupedData[centerKey] = {
          centerName: item.centerName,
          meetingDay: item.meetingDay,
          branchName: item.branchName,
          nextEmiDate: item.nextEmiDate,
          groups: {},
        };
      }

      const groupKey = item.groupName;
      if (!groupedData[centerKey].groups[groupKey]) {
        groupedData[centerKey].groups[groupKey] = {
          groupName: item.groupName,
          members: [],
        };
      }

      groupedData[centerKey].groups[groupKey].members.push(item);
    });

    // In the renderFormattedReport function, fix the table structure
    return Object.values(groupedData).map((center, centerIndex) => {
      const today = new Date();
      const nextMonth = new Date();
      nextMonth.setMonth(today.getMonth() + 1);

      const formatDate = (date) => {
        return date
          .toLocaleDateString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          .replace(/\//g, "-");
      };

      return (
        <div
          key={centerIndex}
          className="report-sheet"
          style={{ marginBottom: "30px", pageBreakAfter: "always" }}
        >
          <table
            className="table table-bordered"
            style={{
              border: "2px solid black",
              borderCollapse: "collapse",
              width: "100%",
            }}
          >
            <thead>
              <tr>
                <th colSpan="13" style={{ textAlign: "center" }}>
                  Office: {center.branchName} | Employee: {selectedCro.label} |
                  Centre: {center.centerName} | Meeting Day{" "}
                  {formatDate(new Date(center.meetingDay))}
                </th>
              </tr>
              <tr>
                <th colSpan="3">Customer Details</th>
                <th colSpan="10">
                  Due Date {formatDate(new Date(center.meetingDay))} | Printed{" "}
                  {formatDate(new Date())} | Next Month EMI{" "}
                  {formatDate(new Date(center.nextEmiDate))}
                </th>
              </tr>
              <tr>
                <th>Member</th>
                <th>Mobile</th>
                <th>LAN</th>
                <th>Product</th>
                <th>Loan Amount</th>
                <th>Disb Date</th>
                <th>POS</th>
                <th>Total EMI</th>
                <th># Paid</th>
                <th>Op. Arrear</th>
                <th>This Month EMI</th>
                <th>Total</th>
                <th>Member Attendance</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(center.groups).map((group, groupIndex) => {
                // Calculate group totals
                let groupLoanAmount = 0;
                let groupPOS = 0;
                let groupTotal = 0;

                group.members.forEach((member) => {
                  groupLoanAmount += parseFloat(member.loanAmount || 0);
                  groupPOS += parseFloat(member.totalOutstandingPrincipal || 0);
                  groupTotal +=
                    member.pendingEmiUptoLastMonth +
                    member.currentMonthPendingEmi;
                });

                return (
                  <React.Fragment key={groupIndex}>
                    {group.members.map((member, memberIndex) => (
                      <tr key={memberIndex}>
                        <td>{member.memberName}</td>
                        <td>{member.mobile}</td>
                        <td>{member.fedLanNo}</td>
                        <td>JLG PL</td>
                        <td>{member.loanAmount}</td>
                        <td>
                          {new Date(member.disbursementDate).toLocaleDateString(
                            "en-US",
                            {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            }
                          )}
                        </td>
                        <td>{member.totalOutstandingPrincipal}</td>
                        <td>{member.tenure}</td>
                        <td>{member.paidEmiCount}</td>
                        <td>{member.pendingEmiUptoLastMonth}</td>
                        <td>{member.currentMonthPendingEmi}</td>
                        <td>
                          {member.pendingEmiUptoLastMonth +
                            member.currentMonthPendingEmi}
                        </td>
                        <td></td>
                      </tr>
                    ))}
                    <tr
                      style={{ backgroundColor: "#f2f2f2", fontWeight: "bold" }}
                    >
                      <td colSpan="4">Group {groupIndex + 1}</td>
                      <td>{Math.round(groupLoanAmount / 1000)}K</td>
                      <td></td>
                      <td>{groupPOS}</td>
                      <td></td>
                      <td></td>
                      <td></td>
                      <td>{groupTotal}</td>
                      <td>{groupTotal}</td>
                      <td></td>
                    </tr>
                  </React.Fragment>
                );
              })}
              <tr style={{ backgroundColor: "#e6e6e6", fontWeight: "bold" }}>
                <td colSpan="4">Total</td>
                <td>
                  {Math.round(
                    reportData.reduce(
                      (sum, item) => sum + parseFloat(item.loanAmount || 0),
                      0
                    ) / 1000
                  )}
                  K
                </td>
                <td></td>
                <td>
                  {reportData.reduce(
                    (sum, item) =>
                      sum + parseFloat(item.totalOutstandingPrincipal || 0),
                    0
                  )}
                </td>
                <td></td>
                <td></td>
                <td></td>
                <td>
                  {reportData.reduce(
                    (sum, item) =>
                      sum +
                      (item.pendingEmiUptoLastMonth +
                        item.currentMonthPendingEmi),
                    0
                  )}
                </td>
                <td>
                  {reportData.reduce(
                    (sum, item) =>
                      sum +
                      (item.pendingEmiUptoLastMonth +
                        item.currentMonthPendingEmi),
                    0
                  )}
                </td>
                <td></td>
              </tr>
              <tr>
                <td colSpan="13">Denomination</td>
              </tr>
              <tr>
                <td></td>
                <td style={{ textAlign: "center" }}>500</td>
                <td style={{ textAlign: "center" }}>200</td>
                <td style={{ textAlign: "center" }}>100</td>
                <td style={{ textAlign: "center" }}>50</td>
                <td style={{ textAlign: "center" }}>20</td>
                <td style={{ textAlign: "center" }}>10</td>
                <td style={{ textAlign: "center" }}>Coins</td>
                <td colSpan="5" style={{ textAlign: "center" }}>
                  Total
                </td>
              </tr>
              <tr>
                <td></td>
                <td
                  style={{ borderBottom: "1px solid black", height: "25px" }}
                ></td>
                <td style={{ borderBottom: "1px solid black" }}></td>
                <td style={{ borderBottom: "1px solid black" }}></td>
                <td style={{ borderBottom: "1px solid black" }}></td>
                <td style={{ borderBottom: "1px solid black" }}></td>
                <td style={{ borderBottom: "1px solid black" }}></td>
                <td style={{ borderBottom: "1px solid black" }}></td>
                <td
                  colSpan="5"
                  style={{ borderBottom: "1px solid black" }}
                ></td>
              </tr>
            </tbody>
            <tfoot>
              <tr style={{ height: "50px" }}>
                <td colSpan="4">Centre leader Signature</td>
                <td colSpan="4">CRO Signature</td>
                <td colSpan="5">BM Signature</td>
              </tr>
            </tfoot>
          </table>
        </div>
      );
    });
  };

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">JLG Collection Sheet Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={fromDate}
                      onChange={(event) => setFromDate(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      placeholder="Enter Date"
                      value={toDate}
                      onChange={(event) => setToDate(event.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={3} md={6} className="mb-2">
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedCro}
                    onChange={setSelectedCro}
                    options={croOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={2} md={6} className="mb-2 d-flex align-items-end">
                  <button
                    className="btn btn-primary w-100"
                    onClick={handleViewReport}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "View Report"}
                  </button>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="p-3">
              {showReport && reportData.length > 0 && (
                <div className="mt-2">
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <button
                      className="btn btn-success"
                      onClick={handleDownloadPdf}
                      disabled={loading}
                    >
                      {loading ? "Generating PDF..." : "Download PDF"}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => window.print()}
                    >
                      Print Report
                    </button>
                  </div>

                  {/* Formatted Report View (similar to image) */}
                  <div className="formatted-report print-section">
                    <h3 className="mb-3">Report Results</h3>
                    {renderFormattedReport()}
                  </div>
                </div>
              )}

              {showReport && reportData.length === 0 && (
                <div className="alert alert-info mt-2">
                  No data found for the selected criteria.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JlgCollectionSheetReport;
